import { Outlet, Link, useLocation } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import { useAuth } from '../../context/AuthContext';
import { paths } from '../../routing/paths';
import { ReactComponent as Logo } from '../../assets/ecgo_logo.svg';
import Button from '@mui/material/Button';
import { useMemo, useState } from 'react';
import {
  AccountCircle,
  AdminPanelSettings,
  HourglassEmpty,
  ManageAccounts,
  Logout,
  MonetizationOn,
  Recycling,
  Rule,
  Smartphone,
  Category,
} from '@mui/icons-material';
import ListItemIcon from '@mui/material/ListItemIcon';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { profileEnabled } from 'config/featureFlags';
import { PointsIcon } from 'components/common/PointsIcon';
import useUser from 'hooks/useUser';
import { UserRole } from '../../types/auth.types';

const drawerWidth = 220;

interface MenuItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

export default function AuthenticatedLayout() {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useUser();
  const { logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuIconStyle = { fontSize: 32 };
  const navItems = useMemo<MenuItem[]>(
    () => [
      ...(user?.role === UserRole.SuperAdmin
        ? [
            { label: 'Annotators', path: paths.reviews, icon: <Rule sx={menuIconStyle} /> },
            {
              label: 'App versions',
              path: paths.appVersions,
              icon: <Smartphone sx={menuIconStyle} />,
            },
            {
              label: 'Bin guidelines',
              path: paths.adminBinGuidelines,
              icon: <Rule sx={menuIconStyle} />,
            },
            {
              label: 'Materials',
              path: paths.adminMaterials,
              icon: <Category sx={menuIconStyle} />,
            },
            {
              label: 'Org admins',
              path: paths.adminOrgAdmins,
              icon: <AdminPanelSettings sx={menuIconStyle} />,
            },
            {
              label: 'Annotator access',
              path: paths.adminAnnotatorAccess,
              icon: <ManageAccounts sx={menuIconStyle} />,
            },
          ]
        : []),
      ...(user?.role === UserRole.Annotator
        ? [
            { label: 'Annotators', path: paths.reviews, icon: <Rule sx={menuIconStyle} /> },
            {
              label: 'App versions',
              path: paths.appVersions,
              icon: <Smartphone sx={menuIconStyle} />,
            },
            {
              label: 'Bin guidelines',
              path: paths.adminBinGuidelines,
              icon: <Rule sx={menuIconStyle} />,
            },
            {
              label: 'Materials',
              path: paths.adminMaterials,
              icon: <Category sx={menuIconStyle} />,
            },
          ]
        : []),
      ...(user?.role === UserRole.OrgAdmin
        ? [
            {
              label: 'Bin guidelines',
              path: paths.adminBinGuidelines,
              icon: <Rule sx={menuIconStyle} />,
            },
          ]
        : []),
      {
        label: 'Recycle',
        path: paths.recycle,
        icon: <Recycling sx={{ fontSize: 32, color: 'inherit' }} />,
      },
      {
        label: 'Points',
        path: paths.rewards,
        icon: <MonetizationOn sx={{ fontSize: 32, color: 'inherit' }} />,
      },
      {
        label: 'Pending',
        path: paths.history,
        icon: <HourglassEmpty sx={{ fontSize: 32, color: 'inherit' }} />,
      },
      ...(profileEnabled
        ? [
            {
              label: 'Profile',
              path: paths.profile,
              icon: <AccountCircle sx={{ fontSize: 32 }} />,
            },
          ]
        : []),
    ],
    [user?.role],
  );

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    logout();
  };

  const drawer = (
    <>
      <Box
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: isMobile ? 'space-between' : 'center',
          p: isMobile ? 2 : 3,
          borderBottom: `4px solid ${theme.palette.grey[100]}`,
        })}
      >
        {isMobile && (
          <IconButton
            onClick={handleDrawerToggle}
            sx={(theme) => ({
              color: theme.palette.common.black,
            })}
          >
            <MenuIcon sx={{ fontSize: 32 }} />
          </IconButton>
        )}
        <Link to={paths.home} style={{ textDecoration: 'none' }}>
          <Logo onClick={() => setMobileOpen(false)} style={{ width: 60, height: 60 }} />
        </Link>
      </Box>
      <Box
        sx={{
          overflow: 'auto',
          height: '100%',
        }}
      >
        <List sx={{ pt: 0 }}>
          {navItems.map((item) => (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                component={Link}
                to={item.path}
                selected={location.pathname === item.path}
                onClick={() => setMobileOpen(false)}
                sx={(theme) => ({
                  color: theme.palette.grey[600],
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.common.white,
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.main,
                    },
                  },
                  p: 2,
                })}
              >
                <ListItemIcon
                  sx={(theme) => ({
                    minWidth: 0,
                    mr: 2,
                    justifyContent: 'center',
                    fontSize: 24,
                    color:
                      location.pathname === item.path
                        ? theme.palette.common.white
                        : theme.palette.common.black,
                  })}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  slotProps={{ primary: { fontSize: 16, fontWeight: 'inherit' } }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
      <List sx={{ pb: 2 }}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: 2,
                fontSize: 16,
                justifyContent: 'center',
              }}
            >
              <Logout sx={{ fontSize: 32 }} />
            </ListItemIcon>
            <ListItemText primary="Logout" slotProps={{ primary: { fontSize: 16 } }} />
          </ListItemButton>
        </ListItem>
      </List>
    </>
  );

  return (
    <Box
      sx={(theme) => ({ display: 'flex', height: '100vh', background: theme.palette.grey[100] })}
    >
      <AppBar
        position="fixed"
        sx={{
          background: 'transparent',
        }}
        elevation={0}
      >
        <Toolbar
          sx={{
            background: 'transparent',
            p: 0,
            pt: 1,
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <IconButton
            onClick={handleDrawerToggle}
            sx={(theme) => ({
              ml: 2,
              color: theme.palette.common.black,
            })}
          >
            <MenuIcon sx={{ fontSize: 32 }} />
          </IconButton>
          {profileEnabled && (
            <Button
              component={Link}
              to={paths.profile}
              sx={(theme) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mr: 2,
                color: theme.palette.common.black,
              })}
            >
              <PointsIcon />
              <Typography sx={{ fontWeight: 600, ml: -1 }}>8</Typography>
              <AccountCircle sx={{ fontSize: 32 }} />
            </Button>
          )}
        </Toolbar>
      </AppBar>
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileOpen : true}
        onClose={handleDrawerToggle}
        elevation={0}
        ModalProps={{ keepMounted: true }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: alpha(theme.palette.common.black, 0.2),
              boxShadow: 'none',
            },
          },
        }}
        sx={{
          pt: 60,
          flexShrink: { sm: 0 },
          [`& .MuiDrawer-paper`]: {
            width: isMobile ? '80%' : drawerWidth,
            boxSizing: 'border-box',
            borderRight: 'none',
            justifyContent: 'space-between',
          },
        }}
      >
        {drawer}
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 2,
          ml: { md: `${drawerWidth}px`, sm: 0 },
          mt: { md: 0, sm: 4 },
          width: { sm: '100%' },
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}
